import { useEffect } from "react";

/**
 * Per-route SEO for a client-rendered SPA. TanStack Router 1.58 has no head
 * manager, so each page calls useSeo() and we upsert the head tags directly —
 * Googlebot renders JS, and prerendered snapshots capture the result for
 * crawlers that don't.
 *
 * The static defaults in index.html remain the fallback for crawlers that
 * don't execute JS; keep them in sync with the home page's values here.
 */

export const SITE_URL = "https://joinconnect.org.au";
export const SITE_NAME = "Connect Sydney";

type SeoOptions = {
  /** Full document title, e.g. "FAQ | Connect Sydney". */
  title: string;
  description?: string;
  /** Route path for the canonical URL, e.g. "/faq". Omit on noindex pages. */
  path?: string;
  /** Private pages: ask crawlers to stay out. */
  noindex?: boolean;
  /**
   * The page's social preview photo — a site-relative path (e.g. from the
   * "Page banners" CMS block) or an absolute URL. Falls back to the home
   * page's hero photo when omitted.
   */
  image?: string;
};

/** Site-relative path ("/images/x.jpg") or absolute URL → always absolute. */
function absoluteUrl(path: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({ title, description, path, noindex, image }: SeoOptions) {
  useEffect(() => {
    document.title = title;
    upsertMeta("property", "og:title", title);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "en_AU");
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:card", "summary_large_image");

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }

    const imageUrl = absoluteUrl(image ?? "/images/hero-group.jpg");
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("name", "twitter:image", imageUrl);

    if (noindex) {
      upsertMeta("name", "robots", "noindex, nofollow");
      document.head.querySelector('link[rel="canonical"]')?.remove();
    } else {
      // Clear a noindex left behind by an SPA navigation from a private page.
      removeMeta("name", "robots");
      if (path !== undefined) {
        const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
        upsertCanonical(canonical);
        upsertMeta("property", "og:url", canonical);
      }
    }
  }, [title, description, path, noindex, image]);
}
